<script setup lang="ts">
import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';

import { MapType, StaticMap } from '../../../maps';
import type { HotelDetailsHeaderData, Stay } from '../../models';
import {
  useHotelDetailsDescriptionStore,
  useHotelDetailsMapStore,
  useHotelDetailsModalsStore,
} from '../../stores';
import HotelAmenitiesSectionLoader from '../loaders/HotelAmenitiesSectionLoader.vue';
import HotelDescriptionOpeningParagraphSectionLoader from '../loaders/HotelDescriptionOpeningParagraphSectionLoader.vue';
import HotelMainDescriptionSectionLoader from '../loaders/HotelMainDescriptionSectionLoader.vue';
import HotelDetailsHeaderLoader from '../loaders/HotelDetailsHeaderLoader.vue';

import HotelAmenitiesSection from './HotelAmenitiesSection.vue';
import HotelDescriptionDistances from './HotelDescriptionDistances.vue';
import HotelDescriptionOpeningParagraphSection from './HotelDescriptionOpeningParagraphSection.vue';
import HotelDescriptionTagsSection from './HotelDescriptionTagsSection.vue';
import HotelDetailsHeader from './HotelDetailsHeader.vue';
import HotelMainDescriptionSection from './HotelMainDescriptionSection.vue';
import HotelCallToActionSection from './HotelCallToActionSection.vue';

interface HotelDetailsDescriptionSectionProps {
  headerData: HotelDetailsHeaderData | undefined;
  source: HotelDetailsEventSource;
  isHearted: boolean;
  isAvailabilityLoading: boolean;
  stay?: Stay;
}

const props = defineProps<HotelDetailsDescriptionSectionProps>();

interface HotelDetailsDescriptionSectionEmits {
  (event: 'continue-with-room'): void;
  (event: 'scroll-to-rooms'): void;
  (event: 'select-hotel'): void;
  (event: 'unselect-hotel'): void;
  (event: 'suggest-another-day'): void;
}

const emit = defineEmits<HotelDetailsDescriptionSectionEmits>();

const hotelDetailsDescriptionStore = useHotelDetailsDescriptionStore();
const hotelDetailsMapStore = useHotelDetailsMapStore();
const hotelDetailsModalsStore = useHotelDetailsModalsStore();
const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

function onOpenAmenitiesModal(): void {
  hotelDetailsModalsStore.openAmenititesModal();
  statsigEventsStore.logHotelDetailsAmenitiesModalOpened({
    source: props.source,
  });
}

function onOpenFullDescriptionModal(): void {
  hotelDetailsModalsStore.openFullHotelDetailsDescriptionModal();
  statsigEventsStore.logHotelDetailsFullDescriptionModalOpened({
    source: props.source,
  });
}

function onOpenPriceGuaranteeModal(): void {
  statsigEventsStore.logHotelDetailsPriceGuaranteeModalOpened({
    source: props.source,
  });
  hotelDetailsModalsStore.openPriceGuaranteeModal();
}

function onSuggestAnotherDate(): void {
  statsigEventsStore.logHotelDetailsWithoutAvailActionTaken({
    source: props.source,
  });
  emit('suggest-another-day');
}
</script>

<template>
  <div class="hotel-details-description-section">
    <div class="left-column">
      <HotelDetailsHeader
        v-if="props.headerData"
        :header-data="props.headerData"
      />
      <HotelDetailsHeaderLoader v-else />
      <HotelDescriptionTagsSection
        v-if="hotelDetailsDescriptionStore.tagsUrls"
        :tag-img-urls="hotelDetailsDescriptionStore.tagsUrls"
        class="hotel-description-tags-section"
      />
      <HotelDescriptionOpeningParagraphSection
        v-if="hotelDetailsDescriptionStore.openingParagraph"
        :opening-paragraph="hotelDetailsDescriptionStore.openingParagraph"
        class="hotel-opening-paragraph-section"
      />
      <HotelDescriptionOpeningParagraphSectionLoader v-else />
      <HotelCallToActionSection
        v-if="browserStore.isSmallerThan1200px && props.headerData"
        :header-data="props.headerData"
        :is-hearted="props.isHearted"
        :stay="props.stay"
        :is-availability-loading="props.isAvailabilityLoading"
        class="call-to-action-section"
        @continue-with-room="emit('continue-with-room')"
        @open-price-guarantee="onOpenPriceGuaranteeModal"
        @scroll-to-rooms="emit('scroll-to-rooms')"
        @select-hotel="emit('select-hotel')"
        @suggest-another-day="onSuggestAnotherDate"
        @unselect-hotel="emit('unselect-hotel')"
      />
      <HotelAmenitiesSection
        v-if="hotelDetailsDescriptionStore.shortAmenitiesGroups"
        :amenities-groups="hotelDetailsDescriptionStore.shortAmenitiesGroups"
        :should-show-more-amenities-button="
          hotelDetailsDescriptionStore.shouldShowMoreAmenitiesButton
        "
        class="hotel-amenities-section"
        @open-amenitites-modal="onOpenAmenitiesModal"
      />
      <HotelAmenitiesSectionLoader v-else class="hotel-amenities-section" />
      <HotelMainDescriptionSection
        v-if="hotelDetailsDescriptionStore.shortMainDescription"
        :description="hotelDetailsDescriptionStore.shortMainDescription"
        @open-full-description-modal="onOpenFullDescriptionModal"
      />
      <HotelMainDescriptionSectionLoader v-else />

      <HotelDescriptionDistances
        v-if="hotelDetailsDescriptionStore.distances"
        :distances="hotelDetailsDescriptionStore.distances"
      />
    </div>
    <div class="right-column">
      <HotelCallToActionSection
        v-if="!browserStore.isSmallerThan1200px && props.headerData"
        :header-data="props.headerData"
        :is-hearted="props.isHearted"
        :stay="props.stay"
        :is-availability-loading="props.isAvailabilityLoading"
        class="call-to-action-section"
        @continue-with-room="emit('continue-with-room')"
        @open-price-guarantee="onOpenPriceGuaranteeModal"
        @scroll-to-rooms="emit('scroll-to-rooms')"
        @select-hotel="emit('select-hotel')"
        @suggest-another-day="onSuggestAnotherDate"
        @unselect-hotel="emit('unselect-hotel')"
      />

      <StaticMap
        :map-configuration="{
          markers: hotelDetailsMapStore.mapMarker,
          markerFormatters: hotelDetailsMapStore.getMapMarkerFormatters(
            MapType.Static
          ),
          startRectangle: hotelDetailsMapStore.initialMapView,
        }"
        :should-lazy-load-map="false"
        class="static-map"
        @zoom="hotelDetailsMapStore.openDynamicMapModal"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-details-description-section {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto;
  gap: var(--spacing-16);

  @include for-less-or-equal-than-900-px {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, min-content);
    gap: 0;
  }

  @include for-less-or-equal-than-1200-px {
    padding: var(--spacing-16) var(--spacing-16) 0 var(--spacing-16);
  }

  .left-column {
    grid-column: 1 / span 7;
    padding: var(--spacing-32) 0;

    @include for-less-or-equal-than-900-px {
      grid-column: 1;
    }

    @include for-less-or-equal-than-1200-px {
      padding: 0;
    }

    .hotel-description-tags-section,
    .hotel-opening-paragraph-section,
    .hotel-amenities-section {
      margin-bottom: var(--spacing-32);
    }
  }

  .right-column {
    grid-column: 8 / -1;

    padding: var(--spacing-32) 0;

    @include for-less-or-equal-than-900-px {
      grid-column: 1;
    }

    @include for-less-or-equal-than-1200-px {
      padding: 0;
    }

    .static-map {
      @include for-less-or-equal-than-900-px {
        margin-top: var(--spacing-32);
      }
    }
  }

  .call-to-action-section {
    margin-bottom: var(--spacing-24);

    @include for-less-or-equal-than-599-px {
      margin-bottom: var(--spacing-32);
    }
  }
}
</style>
